<template>
    <ModalContent>
        <!-- * 공단지사 검색 영역 -->
        <BranchSection>
            <TitleWrapper>
                <DataSectionTitle>공단지사 찾기</DataSectionTitle>
                <ButtonGroup>
                    <RegisterButton @click="registerBranch">
                        <PlusIcon :style="{ paddingTop: '2px' }" /> 공단지사 등록
                    </RegisterButton>
                    <PatientRegisterButton @click="submitPatient" :disabled="!isFormValid">
                        <UserIcon :style="{ paddingTop: '2px' }" /> 환자등록
                    </PatientRegisterButton>
                </ButtonGroup>
            </TitleWrapper>

            <!-- * 선택된 파일 목록 표시 -->
            <FileListSection v-if="selectedFiles.length > 0">
                <FileListTitle>선택된 서류</FileListTitle>
                <FileListContainer>
                    <FileListItem v-for="(file, index) in selectedFiles" :key="index">
                        <FileIcon>
                            <DocumentIcon :size="16" :style="{ paddingTop: '2px' }" />
                        </FileIcon>
                        <FileName>{{ file.fileName || file.name || '파일' }}</FileName>
                        <FileStatus>선택됨</FileStatus>
                    </FileListItem>
                </FileListContainer>
            </FileListSection>

            <SearchContainer>
                <SearchInput v-model="searchQuery" placeholder="지역명 또는 지사명으로 검색" @keyup.enter="searchBranch" />
                <SearchButton @click="searchBranch">
                    <SearchIcon :size="18" />
                </SearchButton>
            </SearchContainer>

            <SearchResultContainer v-if="searchResults.length > 0">
                <SearchResultItem v-for="branch in searchResults" :key="branch.num"
                    :class="{ active: selectedBranch && selectedBranch.num === branch.num }"
                    @click="selectBranch(branch)">
                    <BranchHeader>
                        <BranchType>{{ branch.area }}</BranchType>
                        <BranchName>{{ branch.name }}</BranchName>
                        <ActionButtonGroup>
                            <ActionButton edit @click.stop="editBranch(branch)">
                                <EditIcon :size="16" />
                            </ActionButton>
                            <ActionButton delete @click.stop="deleteBranch(branch)">
                                <DeleteIcon :size="16" />
                            </ActionButton>
                        </ActionButtonGroup>
                    </BranchHeader>
                    <BranchAddress>{{ branch.address }}</BranchAddress>
                    <BranchPhone>{{ branch.fax }}</BranchPhone>
                </SearchResultItem>
            </SearchResultContainer>

            <EmptySearchResult v-else-if="searchPerformed">
                검색 결과가 없습니다. 다른 키워드로 검색해보세요.
            </EmptySearchResult>
        </BranchSection>
    </ModalContent>
</template>

<script>
import * as BranchModalCss from '@/assets/styles/order/modal/BranchModalCss.js';

import SearchIcon from 'vue-material-design-icons/Magnify.vue';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import EditIcon from 'vue-material-design-icons/Pencil.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';
import UserIcon from 'vue-material-design-icons/Account.vue';
import DocumentIcon from 'vue-material-design-icons/FileDocument.vue';

export default {
    name: 'BranchModal',

    components: {
        ...BranchModalCss,
        SearchIcon,
        PlusIcon,
        EditIcon,
        DeleteIcon,
        UserIcon,
        DocumentIcon,
    },

    props: {
        orderId: {
            type: [String, Number],
            required: true
        },
        orderData: {
            type: Object,
            default: () => ({})
        },
        claimType: {
            type: String,
            default: 'diabetes', // 'diabetes' | 'oxygen' | 'assistance'
            validator: value => ['diabetes', 'oxygen', 'assistance'].includes(value)
        },
        initialPatientInfo: {
            type: Object,
            default: () => ({
                name: '',
                residentNum: '',
                phone: '',
                startDate: '',
                endDate: ''
            })
        },
        // 부모에서 선택된 파일들
        selectedFiles: {
            type: Array,
            default: () => []
        },
        products: {
            type: Array,
            default: () => []
        }
    },

    inject: ['globalMessageSystem'],

    emits: [
        'branch-changed',
        'patient-info-changed',
        'form-validated',
        'patient-registered',
        'close'
    ],

    data() {
        return {
            // 환자 정보
            patientInfo: {
                name: '',
                residentNum: '',
                phone: '',
                startDate: '',
                endDate: ''
            },

            // 공단지사 검색
            searchQuery: '',
            searchResults: [],
            searchPerformed: false,
            selectedBranch: null,

            // 유효성 검사
            validation: {
                patientName: { isValid: true, message: '' },
                residentNum: { isValid: true, message: '' },
                phone: { isValid: true, message: '' },
                dateRange: { isValid: true, message: '' },
                branch: { isValid: true, message: '' }
            },
        };
    },

    computed: {
        patientName() {
            return this.patientInfo.name || this.orderData.name || '';
        },

        faxTitle() {
            const typeMap = {
                'diabetes': '당뇨소모성재료',
                'oxygen': '산소발생기',
                'assistance': '보조기기'
            };
            const claimTypeName = typeMap[this.claimType] || '의료기기';
            return `${this.patientName} ${claimTypeName} 환자등록신청`;
        },

        isFormValid() {
            // 공단지사가 선택되었는지 확인
            const hasSelectedBranch = this.selectedBranch !== null;

            // 환자명이 있는지 확인
            const hasPatientName = this.patientName.trim() !== '';

            // 선택된 파일이 있는지 확인
            const hasSelectedFiles = this.selectedFiles && this.selectedFiles.length > 0;

            return hasSelectedBranch && hasPatientName && hasSelectedFiles;
        }
    },

    methods: {
        // Header에 알림 메시지 추가하는 메서드
        addNotificationMessage(message) {
            if (this.globalMessageSystem) {
                this.globalMessageSystem.addMessage({
                    id: Date.now().toString(),
                    title: message.title,
                    text: message.text,
                    type: message.type,
                    isRead: false,
                    timestamp: new Date()
                });
            }
        },

        async convertToScanMode(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = (e) => {
                    const img = new Image();

                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        canvas.width = img.width;
                        canvas.height = img.height;

                        // 안티앨리어싱 비활성화
                        ctx.imageSmoothingEnabled = false;

                        ctx.drawImage(img, 0, 0);

                        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        const data = imageData.data;

                        // =============================
                        // 단일 패스로 모든 처리 (빠른 속도)
                        // =============================
                        for (let i = 0; i < data.length; i += 4) {
                            const r = data[i];
                            const g = data[i + 1];
                            const b = data[i + 2];

                            // 1. 그레이스케일 변환
                            let gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;

                            // 2. 대비 강화 (간단한 곡선)
                            gray = gray / 255.0;
                            gray = gray < 0.5
                                ? 2 * gray * gray  // 어두운 부분 더 어둡게
                                : 1 - 2 * (1 - gray) * (1 - gray);  // 밝은 부분 더 밝게
                            gray = gray * 255;

                            // 3. 강력한 대비 적용
                            gray = (gray - 128) * 2.2 + 128;
                            gray = Math.max(0, Math.min(255, gray));

                            // 4. 이진화 (Otsu 간단 버전 - 고정 임계값)
                            const threshold = 140;
                            const binary = gray > threshold ? 255 : 0;

                            data[i] = binary;
                            data[i + 1] = binary;
                            data[i + 2] = binary;
                            data[i + 3] = 255;
                        }

                        ctx.putImageData(imageData, 0, 0);

                        // PNG로 저장
                        canvas.toBlob(
                            (blob) => {
                                if (!blob) {
                                    reject(new Error("이미지 변환 실패"));
                                    return;
                                }

                                const fileName = file.name.replace(/\.[^/.]+$/, '') + '.png';

                                const scannedFile = new File([blob], fileName, {
                                    type: "image/png",
                                    lastModified: Date.now(),
                                });

                                resolve(scannedFile);
                            },
                            "image/png",
                            0.95  // 약간의 압축으로 속도 향상
                        );
                    };

                    img.onerror = () => reject(new Error("이미지 로드 실패"));
                    img.src = e.target.result;
                };

                reader.onerror = () => reject(new Error("파일 읽기 실패"));
                reader.readAsDataURL(file);
            });
        },

        // 환자등록 처리 (부모에서 전달받은 파일들 사용)
        async submitPatient() {
            if (!this.$globalState.company_fax ||
                !this.$globalState.popbill_id ||
                !this.$globalState.popbill_api_key) {

                this.$swal.fire({
                    icon: 'warning',
                    title: '필수 설정 누락',
                    html: `
                        <div style="text-align: left; margin-bottom: 15px;">
                            <p><strong>환자등록을 위해 다음 설정이 필요합니다:</strong></p>
                            <ul style="margin: 10px 0; padding-left: 20px;">
                                <li style="margin: 5px 0; color: ${!this.$globalState.company_fax ? '#dc3545' : '#28a745'}">
                                    회사 팩스번호: ${this.$globalState.company_fax || '미설정'}
                                </li>
                                <li style="margin: 5px 0; color: ${!this.$globalState.popbill_id ? '#dc3545' : '#28a745'}">
                                    팝빌 ID: ${this.$globalState.popbill_id || '미설정'}
                                </li>
                                <li style="margin: 5px 0; color: ${!this.$globalState.popbill_api_key ? '#dc3545' : '#28a745'}">
                                    팝빌 API 키: ${this.$globalState.popbill_api_key ? '설정됨' : '미설정'}
                                </li>
                            </ul>
                        </div>
                        <p style="color: #6c757d; font-size: 16px;">
                            관리자에게 문의하여 설정을 완료해주세요.
                        </p>
                    `,
                    confirmButtonText: '확인',
                    width: '500px'
                });
                return;
            }

            if (!this.isFormValid) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '필수 정보 누락',
                    text: '공단지사 선택, 환자명 입력, 필수 서류 선택을 모두 완료해주세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            try {
                // 공단지사 선택 확인
                if (!this.selectedBranch || !this.selectedBranch.fax) {
                    throw new Error('공단지사를 선택해주세요.');
                }

                this.showLoading('팩스 전송 중', '공단지사로 서류를 전송 중입니다...');

                // FormData 생성
                const formData = new FormData();

                // 팩스 요청 데이터 추가
                const faxRequestData = {
                    CorpNum: this.$globalState.company_bnum,
                    UserID: this.$globalState.popbill_id,
                    snd: this.$globalState.company_fax,
                    sndnm: this.$globalState.company,
                    title: this.faxTitle,
                    sndDT: '',
                    adsYN: false,
                    fcnt: this.selectedFiles.length.toString(),
                    requestNum: `REQ_${new Date().getTime()}`,
                    rcvs: [
                        {
                            rcv: this.selectedBranch.fax.replace(/-/g, ''), // 하이픈 제거
                            rcvnm: this.selectedBranch.name
                        }
                    ]
                };

                formData.append('form', JSON.stringify(faxRequestData));

                formData.append('company_bnum', this.$globalState.company_bnum || '');

                // 부모에서 전달받은 선택된 파일들 추가 - 동기적으로 처리
                for (const file of this.selectedFiles) {
                    if (file.file) {
                        // 로컬 파일인 경우
                        let processedFile = file.file;

                        // 이미지 파일인 경우 스캔 처리 방식으로 변환
                        if (file.file.type.startsWith('image/')) {
                            try {
                                processedFile = await this.convertToScanMode(file.file);
                            } catch (error) {
                                console.error('이미지 스캔 처리 실패:', error);
                                // 스캔 처리 실패 시 원본 파일 사용
                                processedFile = file.file;
                            }
                        }

                        const fileName = `${this.patientName}_${file.fileName}`;
                        formData.append('file', processedFile, fileName);
                    } else if (file.url) {
                        // 서버에서 가져온 파일인 경우
                        try {
                            const response = await fetch(file.url);
                            let blob = await response.blob();

                            // 이미지 파일인 경우 스캔 처리 방식으로 변환
                            if (blob.type.startsWith('image/')) {
                                try {
                                    const fileObj = new File([blob], file.fileName, { type: blob.type });
                                    const scannedFile = await this.convertToScanMode(fileObj);
                                    blob = scannedFile;
                                } catch (error) {
                                    console.error('이미지 스캔 처리 실패:', error);
                                    // 스캔 처리 실패 시 원본 사용
                                }
                            }

                            const fileName = `${this.patientName}_${file.fileName}`;
                            formData.append('file', blob, fileName);
                        } catch (error) {
                            console.error(`파일 변환 실패: ${file.fileName}`, error);
                            throw new Error(`파일 변환 실패: ${file.fileName}`);
                        }
                    }
                }

                // 요청 전 formData 내용 확인 로깅
                for (let pair of formData.entries()) {
                    console.log(pair[0], pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]);
                }

                // 팩스 전송 요청
                const faxResponse = await fetch(`${this.$apiUrls.hanqmedi}/popbill/fax/sendFax`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'x-pb-userid': this.$globalState.popbill_id || '',
                    }
                });

                // 응답 처리
                if (!faxResponse.ok) {
                    const errorData = await faxResponse.json();
                    throw new Error(errorData.message || '팩스 전송에 실패했습니다.');
                }

                const responseData = await faxResponse.json();

                // 접수번호 추출
                let receiptNum = '';
                if (typeof responseData === 'string') {
                    receiptNum = responseData;
                } else if (responseData && responseData.receiptNum) {
                    receiptNum = responseData.receiptNum;
                } else {
                    receiptNum = `REQ_${new Date().getTime()}`;
                }

                // 팩스 전송 성공 후 청구 상태를 "환자등록"으로 업데이트
                this.showLoading('상태 업데이트 중', '청구 상태를 업데이트 중입니다...');

                try {
                    if (this.claimType === 'diabetes') {
                        // 1. 먼저 당뇨소모성재료 청구 데이터 ID 조회
                        const diabetesResponse = await this.$axios.get(
                            `${this.$apiUrls.hanqmedi}/diabetesClaimData/${this.orderId}`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                }
                            }
                        );

                        if (diabetesResponse.data.success && diabetesResponse.data.data && diabetesResponse.data.data.length > 0) {
                            const diabetesDataId = diabetesResponse.data.data[0].num;

                            // 2. 조회한 ID로 상태 업데이트
                            await this.$axios.put(
                                `${this.$apiUrls.hanqmedi}/diabetesClaimStatus/${diabetesDataId}`,
                                { status: '환자등록' },
                                {
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    }
                                }
                            );
                        } else {
                            // 청구 데이터가 없어도 팩스 전송은 성공했으므로 경고만 표시
                            console.warn('당뇨소모성재료 청구 데이터를 찾을 수 없습니다. 상태 업데이트를 건너뜁니다.');
                        }
                    } else if (this.claimType === 'oxygen') {
                        // 1. 먼저 산소치료 청구 데이터 ID 조회
                        const oxygenResponse = await this.$axios.get(
                            `${this.$apiUrls.hanqmedi}/oxygenClaimData/${this.orderId}`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                }
                            }
                        );

                        if (oxygenResponse.data.success && oxygenResponse.data.data && oxygenResponse.data.data.length > 0) {
                            const oxygenDataId = oxygenResponse.data.data[0].num;

                            // 2. 조회한 ID로 상태 업데이트
                            await this.$axios.put(
                                `${this.$apiUrls.hanqmedi}/oxygenClaimStatus/${oxygenDataId}`,
                                { status: '환자등록' },
                                {
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    }
                                }
                            );
                        } else {
                            // 청구 데이터가 없어도 팩스 전송은 성공했으므로 경고만 표시
                            console.warn('산소치료 청구 데이터를 찾을 수 없습니다. 상태 업데이트를 건너뜁니다.');
                        }
                    }
                } catch (statusUpdateError) {
                    // 상태 업데이트 실패해도 팩스 전송은 성공했으므로 경고만 표시하고 계속 진행
                    console.warn('청구 상태 업데이트 실패:', statusUpdateError);
                    // 에러를 던지지 않고 계속 진행
                }

                const typeMap = {
                    'diabetes': '당뇨소모성재료',
                    'oxygen': '산소발생기',
                    'assistance': '보조기기'
                };
                const claimTypeName = typeMap[this.claimType] || '의료기기';

                this.$swal.fire({
                    title: '환자 등록 완료',
                    html: `
                        <p><strong>${claimTypeName}</strong> 환자 정보가 성공적으로 등록되었습니다.</p>
                        <p>관련 서류가 <strong>${this.selectedBranch.name}</strong>로 팩스 전송되었습니다.</p>
                        <p>접수번호: ${receiptNum}</p>
                        <p>전송된 서류: ${this.selectedFiles.map(f => f.fileName).join(', ')}</p>
                    `,
                    icon: 'success',
                    confirmButtonText: '확인'
                })

                // 부모 컴포넌트에 등록 완료 이벤트 발생
                this.$emit('patient-registered', {
                    receiptNum: receiptNum,
                    branchInfo: this.selectedBranch,
                    patientInfo: {
                        name: this.patientName,
                        ...this.patientInfo
                    },
                    claimType: this.claimType,
                    uploadedFiles: this.selectedFiles
                });

                // 모달 닫기
                this.$emit('close');

            } catch (error) {
                this.handleError(error, '환자 등록 실패', '환자 정보 등록 중 오류가 발생했습니다.');
                throw error;
            } finally {
                if (this.$swal.isLoading()) {
                    this.$swal.close();
                }
            }
        },

        // 폼 유효성 검사
        validateForm() {
            this.$emit('form-validated', this.isFormValid);
        },

        // 공단지사 관련 메서드들
        async searchBranch() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getBranch`,
                    {
                        params: {
                            query: this.searchQuery.trim()
                        },
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (response.data && response.data.success) {
                    this.searchResults = response.data.data.map(branch => ({
                        num: branch.num,
                        area: branch.area,
                        name: branch.name,
                        address: branch.address,
                        fax: this.formatFaxNumber(branch.fax)
                    }));
                } else {
                    this.searchResults = [];
                }
            } catch (error) {
                console.error('지사 검색 오류:', error);
                this.searchResults = [];
            } finally {
                this.searchPerformed = true;
            }
        },

        formatFaxNumber(fax) {
            if (!fax) return '';
            const cleanFax = fax.replace(/-/g, '');
            if (cleanFax.startsWith('02')) {
                return cleanFax.replace(/(\d{2})(\d{3,4})(\d{4})/, '$1-$2-$3');
            }
            return cleanFax.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
        },

        selectBranch(branch) {
            if (this.selectedBranch && this.selectedBranch.num === branch.num) {
                this.selectedBranch = null;
                this.$emit('branch-changed', { branchCode: '', branchName: '' });
            } else {
                this.selectedBranch = { ...branch };
                this.$emit('branch-changed', {
                    branchCode: branch.fax,
                    branchName: branch.name
                });
            }

            this.validateForm();
        },

        // 데이터 초기화
        initializeData() {
            // Props에서 초기 데이터 설정
            if (this.initialPatientInfo) {
                this.patientInfo = { ...this.initialPatientInfo };
            }

            // OrderData에서 초기 데이터 설정
            if (this.orderData) {
                if (!this.patientInfo.name && this.orderData.name) {
                    this.patientInfo.name = this.orderData.name;
                }
                if (!this.patientInfo.phone && this.orderData.phone1) {
                    this.patientInfo.phone = this.orderData.phone1;
                }
            }
        },

        // 공단지사 등록
        async registerBranch() {
            try {
                // 초기 입력값 설정
                let branchData = {
                    area: '',
                    name: '',
                    address: '',
                    fax: ''
                };

                // Swal 입력 폼 표시 함수
                const showBranchInputForm = (data) => {
                    this.$swal.fire({
                        title: '공단지사 등록',
                        html: `
                            <style>
                                .swal2-input-group {
                                    margin-bottom: 12px;
                                    width: 100%;
                                }
                                .swal2-input-group label {
                                    display: block;
                                    text-align: left;
                                    margin-bottom: 10px;
                                    font-size: 20px;
                                    font-weight: 600;
                                    color: #555;
                                }
                                .swal2-input, .swal2-select {
                                    height: 40px !important;
                                    margin: 0 !important;
                                    padding: 0 8px !important;
                                    font-size: 18px !important;
                                    width: 100% !important;
                                    border: 1px solid #d9d9d9;
                                    border-radius: 4px;
                                    box-sizing: border-box;
                                    text-align: left;
                                    margin-bottom: 10px;
                                }
                                .swal2-select {
                                    background-color: white;
                                    background-position: right 8px center;
                                    background-size: 16px;
                                    padding-right: 32px !important;
                                }
                                .swal2-select option {
                                    text-align: left;
                                    padding: 8px;
                                }
                                .address-container {
                                    display: flex;
                                    gap: 8px;
                                }
                                .search-address-btn {
                                    height: 40px;
                                    padding: 0 12px;
                                    font-size: 18px;
                                    background-color: #007bff;
                                    color: #fff;
                                    border: 1px solid #ddd;
                                    border-radius: 4px;
                                    cursor: pointer;
                                }
                                .search-address-btn:hover {
                                    background-color: #e4e4e4;
                                    color: #000;
                                }
                            </style>
                            <div class="swal2-input-group">
                                <label for="branch_area">지역</label>
                                <select id="branch_area" class="swal2-select">
                                    <option value="" disabled selected>지역을 선택하세요</option>
                                    <option value="광주전라제주지역본부" ${data.area === '광주전라제주지역본부' ? 'selected' : ''}>광주전라제주지역본부</option>
                                    <option value="부산울산경남지역본부" ${data.area === '부산울산경남지역본부' ? 'selected' : ''}>부산울산경남지역본부</option>
                                    <option value="서울강원지역본부" ${data.area === '서울강원지역본부' ? 'selected' : ''}>서울강원지역본부</option>
                                    <option value="대구경북지역본부" ${data.area === '대구경북지역본부' ? 'selected' : ''}>대구경북지역본부</option>
                                    <option value="인천경기지역본부" ${data.area === '인천경기지역본부' ? 'selected' : ''}>인천경기지역본부</option>
                                    <option value="대전세종충청지역본부" ${data.area === '대전세종충청지역본부' ? 'selected' : ''}>대전세종충청지역본부</option>
                                </select>
                            </div>
                            <div class="swal2-input-group">
                                <label for="branch_name">지사명</label>
                                <input id="branch_name" class="swal2-input" placeholder="지사명" value="${data.name}">
                            </div>
                            <div class="swal2-input-group">
                                <label for="branch_address">주소</label>
                                <div class="address-container">
                                    <input id="branch_address" class="swal2-input" placeholder="주소" value="${data.address}" style="flex: 1;">
                                    <button type="button" id="search_address" class="search-address-btn">주소검색</button>
                                </div>
                            </div>
                            <div class="swal2-input-group">
                                <label for="branch_fax">팩스번호</label>
                                <input id="branch_fax" class="swal2-input" placeholder="팩스번호 '-' 빼고 입력해주세요." value="${data.fax}">
                            </div>
                        `,
                        customClass: {
                            container: 'branch-register-modal',
                            popup: 'branch-register-popup',
                            title: 'branch-register-title',
                            confirmButton: 'branch-register-confirm',
                            cancelButton: 'branch-register-cancel'
                        },
                        width: '400px',
                        padding: '20px',
                        focusConfirm: false,
                        showCancelButton: true,
                        confirmButtonText: '등록',
                        cancelButtonText: '취소',
                        didOpen: () => {
                            const areaSelect = document.getElementById('branch_area');
                            const nameInput = document.getElementById('branch_name');
                            const addressInput = document.getElementById('branch_address');
                            const faxInput = document.getElementById('branch_fax');

                            if (data.area) areaSelect.value = data.area;
                            if (data.name) nameInput.value = data.name;
                            if (data.address) addressInput.value = data.address;
                            if (data.fax) faxInput.value = data.fax;

                            // 주소검색 버튼 이벤트 리스너
                            document.getElementById('search_address').addEventListener('click', () => {
                                // 현재 입력된 값들 저장
                                branchData.area = areaSelect.value;
                                branchData.name = nameInput.value;
                                branchData.fax = faxInput.value;

                                new window.daum.Postcode({
                                    oncomplete: function (data) {
                                        // 주소 입력 필드만 업데이트
                                        addressInput.value = data.address;
                                        branchData.address = data.address;
                                    }
                                }).open();
                            });

                            // 전체 모달 스타일 조정
                            const style = document.createElement('style');
                            style.innerHTML = `
                                .branch-register-popup {
                                    font-size: 14px;
                                    height: 550px;
                                }
                                .branch-register-title {
                                    font-size: 25px !important;
                                    padding-bottom: 10px;
                                }
                                .branch-register-confirm,
                                .branch-register-cancel {
                                    font-size: 15px !important;
                                    padding: 8px 16px !important;
                                }
                                .swal2-actions {
                                    margin-top: 20px;
                                }
                            `;
                            document.head.appendChild(style);
                        },
                        preConfirm: () => {
                            const area = document.getElementById('branch_area').value;
                            const name = document.getElementById('branch_name').value;
                            const address = document.getElementById('branch_address').value;
                            const fax = document.getElementById('branch_fax').value;

                            // 유효성 검사
                            if (!area || !name || !address || !fax) {
                                this.$swal.showValidationMessage('모든 필드를 입력해주세요.');
                                return false;
                            }

                            return { area, name, address, fax };
                        }
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            const response = await this.$axios.post(
                                `${this.$apiUrls.hanqmedi}/registerBranch`,
                                {
                                    area: result.value.area,
                                    name: result.value.name,
                                    address: result.value.address,
                                    fax: result.value.fax
                                },
                                {
                                    headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                                    }
                                }
                            );

                            if (response.data.success) {
                                this.$swal.fire({
                                    icon: 'success',
                                    title: '등록 완료',
                                    text: '공단지사가 성공적으로 등록되었습니다.',
                                    timer: 1500
                                });
                                // 검색 결과 갱신
                                this.searchBranch();
                            } else {
                                throw new Error(response.data.message || '등록에 실패했습니다.');
                            }
                        }
                    });
                };

                // 초기 입력 폼 표시
                showBranchInputForm(branchData);

            } catch (error) {
                console.error('공단지사 등록 오류:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '등록 오류',
                    text: error.message || '공단지사 등록 중 오류가 발생했습니다.',
                    timer: 2000
                });
            }
        },

        // 공단지사 수정
        async editBranch(branch) {
            try {
                // 선택된 지사 데이터로 초기화
                let branchData = {
                    num: branch.num,
                    area: branch.area,
                    name: branch.name,
                    address: branch.address,
                    fax: branch.fax
                };

                const result = await this.$swal.fire({
                    title: '공단지사 수정',
                    html: `
                        <style>
                            .swal2-input-group {
                                margin-bottom: 12px;
                                width: 100%;
                            }
                            .swal2-input-group label {
                                display: block;
                                text-align: left;
                                margin-bottom: 10px;
                                font-size: 20px;
                                font-weight: 600;
                                color: #555;
                            }
                            .swal2-input, .swal2-select {
                                height: 40px !important;
                                margin: 0 !important;
                                padding: 0 8px !important;
                                font-size: 18px !important;
                                width: 100% !important;
                                border: 1px solid #d9d9d9;
                                border-radius: 4px;
                                box-sizing: border-box;
                                text-align: left;
                                margin-bottom: 10px;
                            }
                            .swal2-select {
                                background-color: white;
                                background-position: right 8px center;
                                background-size: 16px;
                                padding-right: 32px !important;
                            }
                            .swal2-select option {
                                text-align: left;
                                padding: 8px;
                            }
                            .address-container {
                                display: flex;
                                gap: 8px;
                            }
                            .search-address-btn {
                                height: 40px;
                                padding: 0 12px;
                                font-size: 18px;
                                background-color: #007bff;
                                color: #fff;
                                border: 1px solid #ddd;
                                border-radius: 4px;
                                cursor: pointer;
                            }
                            .search-address-btn:hover {
                                background-color: #e4e4e4;
                                color: #000;
                            }
                        </style>
                        <div class="swal2-input-group">
                            <label for="branch_area">지역</label>
                            <select id="branch_area" class="swal2-select">
                                <option value="" disabled>지역을 선택하세요</option>
                                <option value="광주전라제주지역본부" ${branchData.area === '광주전라제주지역본부' ? 'selected' : ''}>광주전라제주지역본부</option>
                                <option value="부산울산경남지역본부" ${branchData.area === '부산울산경남지역본부' ? 'selected' : ''}>부산울산경남지역본부</option>
                                <option value="서울강원지역본부" ${branchData.area === '서울강원지역본부' ? 'selected' : ''}>서울강원지역본부</option>
                                <option value="대구경북지역본부" ${branchData.area === '대구경북지역본부' ? 'selected' : ''}>대구경북지역본부</option>
                                <option value="인천경기지역본부" ${branchData.area === '인천경기지역본부' ? 'selected' : ''}>인천경기지역본부</option>
                                <option value="대전세종충청지역본부" ${branchData.area === '대전세종충청지역본부' ? 'selected' : ''}>대전세종충청지역본부</option>
                            </select>
                        </div>
                        <div class="swal2-input-group">
                            <label for="branch_name">지사명</label>
                            <input id="branch_name" class="swal2-input" placeholder="지사명" value="${branchData.name}">
                        </div>
                        <div class="swal2-input-group">
                            <label for="branch_address">주소</label>
                            <div class="address-container">
                                <input id="branch_address" class="swal2-input" placeholder="주소" value="${branchData.address}" style="flex: 1;">
                                <button type="button" id="search_address" class="search-address-btn">주소검색</button>
                            </div>
                        </div>
                        <div class="swal2-input-group">
                            <label for="branch_fax">팩스번호</label>
                            <input id="branch_fax" class="swal2-input" placeholder="팩스번호 '-' 빼고 입력해주세요." value="${branchData.fax}">
                        </div>
                    `,
                    customClass: {
                        container: 'branch-register-modal',
                        popup: 'branch-register-popup',
                        title: 'branch-register-title',
                        confirmButton: 'branch-register-confirm',
                        cancelButton: 'branch-register-cancel'
                    },
                    width: '400px',
                    padding: '20px',
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: '수정',
                    cancelButtonText: '취소',
                    didOpen: () => {
                        // 주소검색 버튼 이벤트 리스너
                        document.getElementById('search_address').addEventListener('click', () => {
                            // 현재 입력값 저장
                            const areaSelect = document.getElementById('branch_area');
                            const nameInput = document.getElementById('branch_name');
                            const faxInput = document.getElementById('branch_fax');

                            branchData.area = areaSelect.value;
                            branchData.name = nameInput.value;
                            branchData.fax = faxInput.value;

                            new window.daum.Postcode({
                                oncomplete: function (data) {
                                    document.getElementById('branch_address').value = data.address;
                                    branchData.address = data.address;
                                }
                            }).open();
                        });
                    },
                    preConfirm: () => {
                        const area = document.getElementById('branch_area').value;
                        const name = document.getElementById('branch_name').value;
                        const address = document.getElementById('branch_address').value;
                        const fax = document.getElementById('branch_fax').value;

                        if (!area || !name || !address || !fax) {
                            this.$swal.showValidationMessage('모든 필드를 입력해주세요.');
                            return false;
                        }

                        return { area, name, address, fax };
                    }
                });

                if (result.isConfirmed) {
                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/editBranch/${branchData.num}`,
                        {
                            area: result.value.area,
                            name: result.value.name,
                            address: result.value.address,
                            fax: result.value.fax
                        },
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        }
                    );

                    if (response.data.success) {
                        await this.$swal.fire({
                            icon: 'success',
                            title: '수정 완료',
                            text: '공단지사 정보가 성공적으로 수정되었습니다.',
                            timer: 1500
                        });
                        this.searchBranch();
                    } else {
                        throw new Error(response.data.message || '수정에 실패했습니다.');
                    }
                }
            } catch (error) {
                console.error('공단지사 수정 오류:', error);
                await this.$swal.fire({
                    icon: 'error',
                    title: '수정 오류',
                    text: error.message || '공단지사 수정 중 오류가 발생했습니다.',
                    timer: 2000
                });
            }
        },

        // 공단지사 삭제
        async deleteBranch(branch) {
            try {
                const confirmResult = await this.$swal.fire({
                    icon: 'warning',
                    title: '공단지사 삭제',
                    text: `${branch.name} 지사를 삭제하시겠습니까?`,
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#dc3545',
                    reverseButtons: true
                });

                if (!confirmResult.isConfirmed) {
                    return;
                }

                const response = await this.$axios.delete(
                    `${this.$apiUrls.hanqmedi}/deleteBranch/${branch.num}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                    }
                );

                if (response.data.success) {
                    this.$swal.fire({
                        icon: 'success',
                        title: '삭제 완료',
                        text: '공단지사가 성공적으로 삭제되었습니다.',
                        timer: 1500
                    });
                    this.searchBranch();
                } else {
                    throw new Error(response.data.message || '삭제에 실패했습니다.');
                }
            } catch (error) {
                console.error('공단지사 삭제 오류:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '삭제 오류',
                    text: error.message || '공단지사 삭제 중 오류가 발생했습니다.',
                    timer: 2000
                });
            }
        },

        showLoading(title, text) {
            this.$swal.fire({
                title,
                text,
                allowOutsideClick: false,
                didOpen: () => {
                    this.$swal.showLoading();
                }
            });
        },

        handleError(error, title, defaultMessage) {
            console.error(`${title}:`, error);
            this.$swal.fire({
                icon: 'error',
                title,
                text: error.message || defaultMessage,
                confirmButtonText: '확인'
            });
        }
    },

    watch: {
        // 환자 정보 변경 감지
        patientInfo: {
            deep: true,
            handler(newValue) {
                this.$emit('patient-info-changed', newValue);
                this.validateForm();
            }
        },

        // Props 변경 감지
        initialPatientInfo: {
            deep: true,
            immediate: true,
            handler() {
                this.initializeData();
            }
        }
    },

    mounted() {
        this.initializeData();
        this.searchBranch(); // 초기 공단지사 목록 로드
        this.validateForm();
    }
};
</script>